import type { Ref, WritableComputedRef } from 'vue'
import type { AppSelectOption, SelectPaginatedData } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { AppSelectPaginatedFetcherParams } from '@shared/ui/selects'

import { useTutorsListApi } from '@entities/tutor'
import { useAppSelectPaginated } from '@shared/ui/selects'

interface DisciplineTutorSelectOptions {
  tutorId: Ref<number | null>
}

interface DisciplineTutorSelectReturn {
  refTutorId: WritableComputedRef<number | undefined>
  tutorOptions: ComputedRef<AppSelectOption<number>[]>
  selectedTutorOption: ComputedRef<AppSelectOption<number> | undefined>
  tutorHasMore: ComputedRef<boolean>
  tutorLoading: Readonly<Ref<boolean>>
  onTutorSearch: (query: string) => void
  onTutorLoadMore: (query: string) => void
  onTutorOpen: () => void
}

/** A single tutor picker over the ACTIVE PAID tutors — the list the legacy task modals offered. The
 *  chosen tutor is sent as `preselected`, so its label resolves even off the first page. */
export const useDisciplineTutorSelect = ({ tutorId }: DisciplineTutorSelectOptions): DisciplineTutorSelectReturn => {
  const selectedCache = ref<AppSelectOption<number> | undefined>(undefined)

  const refTutorId = computed<number | undefined>({
    get: () => tutorId.value ?? undefined,
    set: (value) => {
      tutorId.value = value ?? null
    },
  })

  const fetcher = ({ query, page, signal }: AppSelectPaginatedFetcherParams): Promise<SelectPaginatedData | null> =>
    useTutorsListApi({
      query,
      page,
      signal,
      active: true,
      paid: true,
      preselected: tutorId.value ? [tutorId.value] : undefined,
    })

  const {
    options,
    hasMore: tutorHasMore,
    loading: tutorLoading,
    onSearch: onTutorSearch,
    onLoadMore: onTutorLoadMore,
    onOpen: onTutorOpen,
  } = useAppSelectPaginated(fetcher)

  const tutorOptions = computed<AppSelectOption<number>[]>(() => options.value as AppSelectOption<number>[])

  const selectedTutorOption = computed<AppSelectOption<number> | undefined>(() =>
    tutorOptions.value.find(option => option.value === tutorId.value) ?? selectedCache.value)

  watch(selectedTutorOption, (option) => {
    if (option) selectedCache.value = option
  })

  return {
    refTutorId,
    tutorOptions,
    selectedTutorOption,
    tutorHasMore,
    tutorLoading,
    onTutorSearch,
    onTutorLoadMore,
    onTutorOpen,
  }
}
